import {
  type TCreateStructuredLogUserEngagement,
  type IStructuredLogUserEngagement,
  type IStructuredLogUserEngagementOptions,
} from '../types/adscopilot-structured-log-types';
import { type ICreateStructuredLogParams } from '../types/structured-log-types';
import { createStructuredLogBase } from './create-structured-log-base';

type TCreateStructuredLogUserEngagementResult = Pick<IStructuredLogUserEngagement, 'activityType' | 'activityText' | 'additionalContext'>;

const getStructuredLogStartOverClicked = (): TCreateStructuredLogUserEngagementResult => ({
  activityType: 'StartOverClicked',
  activityText: 'User clicked on start over button to start new chat.',
});

const getStructuredLogConnectToAgentRequested = (): TCreateStructuredLogUserEngagementResult => ({
  activityType: 'ConnectToAgentRequested',
  activityText: 'User requested for connect to an agent.',
});

const getStructuredLogConversationResume = (): TCreateStructuredLogUserEngagementResult => ({
  activityType: 'ConversationResume',
  activityText: 'Conversation resumed with Copilot.',
});

const getStructuredLogThumbsUpClicked = (options: NonNullable<IStructuredLogUserEngagementOptions['thumbsUpClicked']>): TCreateStructuredLogUserEngagementResult => {
  const isReactionReverted = options.currentReaction === 'None';
  return {
    activityType: 'ThumbsUpClicked',
    activityText: `User ${isReactionReverted ? 'un-clicked' : 'clicked'} on ThumbsUp button.`,
    additionalContext: options,
  };
};

const getStructuredLogThumbsDownClicked = (options: NonNullable<IStructuredLogUserEngagementOptions['thumbsDownClicked']>): TCreateStructuredLogUserEngagementResult => {
  const isReactionReverted = options.currentReaction === 'None';
  return {
    activityType: 'ThumbsDownClicked',
    activityText: `User ${isReactionReverted ? 'un-clicked' : 'clicked'} on ThumbsDown button.`,
    additionalContext: options,
  };
};

const getStructuredLogThumbsDownFeedback = (options: NonNullable<IStructuredLogUserEngagementOptions['thumbsDownFeedback']>): TCreateStructuredLogUserEngagementResult => ({
  activityType: 'ThumbsDownFeedback',
  activityText: 'User provided thumbs down feedback.',
  additionalContext: options,
});

const getStructuredLogCopyClicked = (options: NonNullable<IStructuredLogUserEngagementOptions['copyClicked']>): TCreateStructuredLogUserEngagementResult => ({
  activityType: 'CopyClicked',
  activityText: 'User clicked on copy button.',
  additionalContext: options,
});

const getStructuredLogMessagePopoverClicked = (options: NonNullable<IStructuredLogUserEngagementOptions['messagePopoverClicked']>): TCreateStructuredLogUserEngagementResult => ({
  activityType: 'MessagePopoverClicked',
  activityText: 'User clicked on message popover.',
  additionalContext: options,
});

const getStructuredLogLearnMoreLinkClicked = (options: NonNullable<IStructuredLogUserEngagementOptions['learnMoreLinkClicked']>): TCreateStructuredLogUserEngagementResult => ({
  activityType: 'LearnMoreLinkClicked',
  activityText: 'User clicked on learn more link.',
  additionalContext: options,
});

const getStructuredLogSiteLinkClicked = (options: NonNullable<IStructuredLogUserEngagementOptions['siteLinkClicked']>): TCreateStructuredLogUserEngagementResult => ({
  activityType: 'SiteLinkClicked',
  activityText: 'User clicked on site link.',
  additionalContext: options,
});

const getStructuredLogLiveAgentQueueSelected = (options: NonNullable<IStructuredLogUserEngagementOptions['liveAgentQueueSelected']>): TCreateStructuredLogUserEngagementResult => ({
  activityType: 'LiveAgentQueueSelected',
  activityText: `User selected live agent queue: ${options.queue}.`,
  additionalContext: options,
});

const getStructuredLogLiveAgentPostChatSurvey = (options: NonNullable<IStructuredLogUserEngagementOptions['liveAgentPostChatSurveyResponse']>): TCreateStructuredLogUserEngagementResult => ({
  activityType: 'LiveAgentPostChatSurvey',
  activityText: 'User submitted live agent post chat survey.',
  additionalContext: options,
});

const getStructuredLogInChatFeedbackSurvey = (options: NonNullable<IStructuredLogUserEngagementOptions['inChatFeedbackSurveyResponse']>): TCreateStructuredLogUserEngagementResult => ({
  activityType: 'InChatFeedbackSurvey',
  activityText: 'User submitted in-chat feedback survey.',
  additionalContext: options,
});

const getStructuredLogInChatFeedbackSurveyDismissed = (options: NonNullable<IStructuredLogUserEngagementOptions['inChatFeedbackSurveyDismissed']>): TCreateStructuredLogUserEngagementResult => ({
  activityType: 'InChatFeedbackSurveyDismissed',
  activityText: 'User dismissed in-chat feedback survey without submitting.',
  additionalContext: options,
});

const getStructuredLogInChatFeedbackSurveyShown = (options: NonNullable<IStructuredLogUserEngagementOptions['inChatFeedbackSurveyShown']>): TCreateStructuredLogUserEngagementResult => ({
  activityType: 'InChatFeedbackSurveyShown',
  activityText: `In-chat feedback survey shown for ${options.feedbackType}.`,
  additionalContext: options,
});

const getStructuredLogPromptStarterItemClicked = (options: NonNullable<IStructuredLogUserEngagementOptions['promptStarterItemClicked']>): TCreateStructuredLogUserEngagementResult => ({
  activityType: 'PromptStarterItemClicked',
  activityText: `User clicked on prompt starter item: ${options.text}.`,
  additionalContext: options,
});

const getStructuredLogPromptLibraryItemClicked = (options: NonNullable<IStructuredLogUserEngagementOptions['promptLibraryItemClicked']>): TCreateStructuredLogUserEngagementResult => ({
  activityType: 'PromptLibraryItemClicked',
  activityText: `User clicked on prompt library item: ${options.text}.`,
  additionalContext: options,
});

const getStructuredLogPromptPreferenceClicked = (options: NonNullable<IStructuredLogUserEngagementOptions['promptPreferenceClicked']>): TCreateStructuredLogUserEngagementResult => ({
  activityType: 'PromptPreferenceClicked',
  activityText: `User ${options.currentPreference === 'saved' ? 'saved' : 'unsaved'} a prompt preference: ${options.text}.`,
  additionalContext: options,
});

const getStructuredLogPromptPreferenceSavedCount = (options: NonNullable<IStructuredLogUserEngagementOptions['promptPreferenceSavedCount']>): TCreateStructuredLogUserEngagementResult => ({
  activityType: 'PromptPreferenceSavedCount',
  activityText: `User has ${options.savedCount} saved items in prompt library.`,
  additionalContext: options,
});

const getStructuredLogSignInLinkClicked = (options: NonNullable<IStructuredLogUserEngagementOptions['signInLinkClicked']>): TCreateStructuredLogUserEngagementResult => ({
  activityType: 'SignInLinkClicked',
  activityText: 'User clicked on sign in link.',
  additionalContext: options,
});

const getStructuredLogSignUpLinkClicked = (options: NonNullable<IStructuredLogUserEngagementOptions['signUpLinkClicked']>): TCreateStructuredLogUserEngagementResult => ({
  activityType: 'SignUpLinkClicked',
  activityText: 'User clicked on sign up link.',
  additionalContext: options,
});

const getStructuredLogTestYourTagToolWebsiteConfirmed = (options: NonNullable<IStructuredLogUserEngagementOptions['testYourTagToolWebsiteConfirmed']>): TCreateStructuredLogUserEngagementResult => ({
  activityType: 'TestYourTagToolWebsiteConfirmed',
  activityText: `User confirmed website for Test Your Tag tool with tag ID: ${options.tagId}, website URL: ${options.websiteUrl}.`,
  additionalContext: options,
});

const getStructuredLogTestYourTagToolCompleted = (options: NonNullable<IStructuredLogUserEngagementOptions['testYourTagToolCompleted']>): TCreateStructuredLogUserEngagementResult => ({
  activityType: 'TestYourTagToolCompleted',
  activityText: `User completed Test Your Tag tool with tag ID: ${options.tagId}, session ID: ${options.sessionId}.`,
  additionalContext: options,
});

const getStructuredLogCaptchaEvent = (options: NonNullable<IStructuredLogUserEngagementOptions['captchaEvent']>): TCreateStructuredLogUserEngagementResult => ({
  activityType: 'CaptchaEvent',
  activityText: `Captcha event: ${options.action}.`,
  additionalContext: options,
});

const getStructuredLogSource = (options: NonNullable<IStructuredLogUserEngagementOptions['chatTriggerSource']>): TCreateStructuredLogUserEngagementResult => ({
  activityType: 'ChatTriggerSource',
  activityText: `User opened copilot chat from: ${options.source}.`,
  additionalContext: options,
});

const getStructuredLogUserEngagement = (
  activity?: ICreateStructuredLogParams['activity'],
  options?: IStructuredLogUserEngagementOptions
): TCreateStructuredLogUserEngagementResult => {
  if ((activity as any).type === 'endOfConversation') {
    return getStructuredLogStartOverClicked();
  }

  if (['directHandoffInitiate', 'handoffInitiate'].includes((activity as any).name)) {
    return getStructuredLogConnectToAgentRequested();
  }

  if ((activity as any).name === 'conversationResume') {
    return getStructuredLogConversationResume();
  }

  if (options?.thumbsUpClicked) {
    return getStructuredLogThumbsUpClicked(options.thumbsUpClicked);
  }

  if (options?.thumbsDownClicked) {
    return getStructuredLogThumbsDownClicked(options.thumbsDownClicked);
  }

  if (options?.thumbsDownFeedback) {
    return getStructuredLogThumbsDownFeedback(options.thumbsDownFeedback);
  }

  if (options?.copyClicked) {
    return getStructuredLogCopyClicked(options.copyClicked);
  }

  if (options?.messagePopoverClicked) {
    return getStructuredLogMessagePopoverClicked(options.messagePopoverClicked);
  }

  if (options?.learnMoreLinkClicked) {
    return getStructuredLogLearnMoreLinkClicked(options.learnMoreLinkClicked);
  }

  if (options?.siteLinkClicked) {
    return getStructuredLogSiteLinkClicked(options.siteLinkClicked);
  }

  if (options?.liveAgentQueueSelected) {
    return getStructuredLogLiveAgentQueueSelected(options.liveAgentQueueSelected);
  }

  if (options?.liveAgentPostChatSurveyResponse) {
    return getStructuredLogLiveAgentPostChatSurvey(options.liveAgentPostChatSurveyResponse);
  }

  if (options?.inChatFeedbackSurveyResponse) {
    return getStructuredLogInChatFeedbackSurvey(options.inChatFeedbackSurveyResponse);
  }

  if (options?.inChatFeedbackSurveyDismissed) {
    return getStructuredLogInChatFeedbackSurveyDismissed(options.inChatFeedbackSurveyDismissed);
  }

  if (options?.inChatFeedbackSurveyShown) {
    return getStructuredLogInChatFeedbackSurveyShown(options.inChatFeedbackSurveyShown);
  }

  if (options?.chatTriggerSource) {
    return getStructuredLogSource(options.chatTriggerSource);
  }

  if (options?.promptStarterItemClicked) {
    return getStructuredLogPromptStarterItemClicked(options.promptStarterItemClicked);
  }

  if (options?.promptLibraryItemClicked) {
    return getStructuredLogPromptLibraryItemClicked(options.promptLibraryItemClicked);
  }

  if (options?.promptPreferenceClicked) {
    return getStructuredLogPromptPreferenceClicked(options.promptPreferenceClicked);
  }

  if (options?.promptPreferenceSavedCount) {
    return getStructuredLogPromptPreferenceSavedCount(options.promptPreferenceSavedCount);
  }

  if (options?.signInLinkClicked) {
    return getStructuredLogSignInLinkClicked(options.signInLinkClicked);
  }

  if (options?.signUpLinkClicked) {
    return getStructuredLogSignUpLinkClicked(options.signUpLinkClicked);
  }

  if (options?.testYourTagToolWebsiteConfirmed) {
    return getStructuredLogTestYourTagToolWebsiteConfirmed(options.testYourTagToolWebsiteConfirmed);
  }

  if (options?.testYourTagToolCompleted) {
    return getStructuredLogTestYourTagToolCompleted(options.testYourTagToolCompleted);
  }

  if (options?.captchaEvent) {
    return getStructuredLogCaptchaEvent(options.captchaEvent);
  }

  return {
    activityType: 'Unknown',
    activityText: 'Unknown user engagement activity recorded.',
    additionalContext: { activity },
  };
};

export const createStructureLogUserEngagement: TCreateStructuredLogUserEngagement = (params) => {
  const { options, ...restParams } = params;
  const { activity } = restParams;

  const structuredLogBase = createStructuredLogBase(restParams);
  const structuredLogUserEngagement = getStructuredLogUserEngagement(activity, options);

  return {
    ...structuredLogBase,
    ...structuredLogUserEngagement,
    additionalContext: {
      ...structuredLogBase.additionalContext,
      ...structuredLogUserEngagement.additionalContext,
    },
  };
};
